// Autonomous bot AI. THE differentiator vs the legacy game, where NPCs were
// passive targets that only reacted when the player walked into them. Here every
// bot decides an action each world-step by archetype, so the island stays alive
// for a solo player: bots hunt, loot, flee the shrinking zone, and fight EACH
// OTHER. A lightweight utility/state-machine — cheap enough to port to a Celery
// world tick on the backend later.

import {
  hpRatio,
  isForbidden,
  nextForbiddenZones,
  occupants,
  safeZones,
  zonePopulation,
} from './JKGame_world'
import type { Entity, MatchState } from './JKGame_types'

export type BotAction =
  | { type: 'move'; zone: number }
  | { type: 'search' }
  | { type: 'rest'; kind: 'sleep' | 'heal' }
  | { type: 'attack'; targetId: string }
  | { type: 'idle' }

const BOT_MOVE_SP = 15

function weakest(list: Entity[]): Entity {
  return list.reduce((a, b) => (b.hp < a.hp ? b : a))
}

function lowestPopZone(match: MatchState, self: Entity): number | null {
  const pop = zonePopulation(match)
  const zones = safeZones(match).filter((z) => z !== self.zone)
  if (zones.length === 0) return null
  zones.sort((a, b) => pop[a] - pop[b])
  return zones[0]
}

function highestPreyZone(match: MatchState, self: Entity): number | null {
  const pop = zonePopulation(match)
  let best = -1
  let bestPop = 0
  for (const z of safeZones(match)) {
    const prey = z === self.zone ? pop[z] - 1 : pop[z]
    if (prey > bestPop) {
      bestPop = prey
      best = z
    }
  }
  return bestPop > 0 ? best : null
}

export function decideAction(self: Entity, match: MatchState, rng: import('./JKGame_rng').JKGameRng): BotAction {
  // 1) Survival first: flee the collar if standing in (or about to stand in) a禁區.
  const closingSoon = match.stepsToShrink <= 1 && nextForbiddenZones(match).includes(self.zone)
  if (isForbidden(match, self.zone) || closingSoon) {
    const z = lowestPopZone(match, self)
    if (z !== null) return { type: 'move', zone: z }
  }

  const hostiles = occupants(match, self.zone, self.id)
  const hp = hpRatio(self)
  const aggressive = self.archetype === 'hunter' || self.archetype === 'veteran' || self.archetype === 'boss'

  // 2) Out of stamina and safe → sleep.
  if (self.sp <= BOT_MOVE_SP && hostiles.length === 0) return { type: 'rest', kind: 'sleep' }

  // 3) Badly hurt.
  if (hp < 0.3) {
    if (hostiles.length > 0) {
      if (aggressive) return { type: 'attack', targetId: weakest(hostiles).id }
      const z = lowestPopZone(match, self)
      if (z !== null) return { type: 'move', zone: z }
      return { type: 'attack', targetId: weakest(hostiles).id } // cornered
    }
    return { type: 'rest', kind: 'heal' }
  }

  // 4) Enemies present.
  if (hostiles.length > 0) {
    if (aggressive) return { type: 'attack', targetId: weakest(hostiles).id }
    if (self.archetype === 'looter' && self.weapon.kind !== 'N') {
      return { type: 'attack', targetId: weakest(hostiles).id }
    }
    // cautious (survivor / medic / unarmed looter) → flee if possible.
    const z = lowestPopZone(match, self)
    if (z !== null) return { type: 'move', zone: z }
    return { type: 'attack', targetId: weakest(hostiles).id } // cornered
  }

  // 5) Idle zone → behave by archetype.
  switch (self.archetype) {
    case 'looter':
      return { type: 'search' }
    case 'medic':
      return hp < 0.7 ? { type: 'rest', kind: 'heal' } : { type: 'search' }
    case 'survivor': {
      if (hp < 0.7 && rng.pct(50)) return { type: 'rest', kind: 'heal' }
      const z = lowestPopZone(match, self)
      if (z !== null && rng.pct(45)) return { type: 'move', zone: z }
      return { type: 'rest', kind: 'sleep' }
    }
    case 'hunter':
    case 'veteran': {
      const z = highestPreyZone(match, self)
      if (z !== null && z !== self.zone) return { type: 'move', zone: z }
      return { type: 'search' }
    }
    case 'boss': {
      if (rng.pct(30)) {
        const z = lowestPopZone(match, self)
        if (z !== null) return { type: 'move', zone: z }
      }
      return { type: 'search' }
    }
    default:
      return { type: 'idle' }
  }
}
